import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import PortfolioRequired from '../components/PortfolioRequired';
import PortfolioMetrics from '../components/PortfolioMetrics';
import StockAccountingWarning from '../components/StockAccountingWarning';
import PortfolioBreakdownBar from '../components/PortfolioBreakdownBar';
import CategoryBreakdown from '../components/CategoryBreakdown';
import LocationBreakdown from '../components/LocationBreakdown';
import { usePortfolioContext } from '../contexts/PortfolioContext';
import { GET_STOCK_DASHBOARD } from '../graphql/queries/stocks';
import { STOCK_POSITIONS_ROUTE } from '../constants/routes';
import { parseFloatFromString } from '../utils/formatters';
import {
  instrumentTypeLabel,
  stockCategoriesBy,
  stockLocationCategories,
  stockSectorLabel,
  stockSectorValue,
  stockInstrumentTypeValue,
} from '../utils/stockBreakdowns';

const openSearch = () => window.dispatchEvent(new Event('open-search'));

const StocksDashboardPage = () => {
  const navigate = useNavigate();
  const { currentPortfolioId, currentPortfolio } = usePortfolioContext();

  const { loading, error, data } = useQuery(GET_STOCK_DASHBOARD, {
    variables: { portfolioId: currentPortfolioId },
    skip: !currentPortfolioId,
    fetchPolicy: 'cache-and-network',
  });

  if (!currentPortfolioId) {
    return (
      <PortfolioRequired
        title="Welcome to Your Stocks Dashboard"
        description="Select a portfolio to view its stocks and funds."
      />
    );
  }

  if (loading && !data) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="px-2 sm:px-3 lg:px-4 py-2">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
          <strong className="font-bold">Couldn't load stocks.</strong>
          <span className="block sm:inline"> {error.message}</span>
        </div>
      </div>
    );
  }

  const summary = data?.stockPortfolioSummary;
  const positions = data?.stockPositions || [];
  const accountingUnavailable = positions.some((p) => p.transactions?.some((tx) => tx.accountingWarning));
  const openPositions = positions.filter((p) => parseFloatFromString(p.holdingsAmount || '0') > 0);
  const typeCategories = stockCategoriesBy(openPositions, (p) => instrumentTypeLabel(p.token?.instrumentType), (p) => stockInstrumentTypeValue(p.token));
  const sectorCategories = stockCategoriesBy(openPositions, (p) => stockSectorLabel(p.token), (p) => stockSectorValue(p.token));
  const locationCategories = stockLocationCategories(openPositions);
  const transactionsCount = positions.reduce((sum, p) => sum + (p.transactions?.length || 0), 0);

  return (
    <div className="px-2 sm:px-3 lg:px-4 py-2 space-y-4">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-blue-600 dark:text-blue-400">
          Stocks
        </p>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          {currentPortfolio?.name || 'Portfolio'} dashboard
        </h1>
      </div>

      {accountingUnavailable && <StockAccountingWarning />}
      {positions.length === 0 ? (
        <section className="bg-gray-100 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            No stock positions yet
          </h2>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            Find a stock, ETF or fund and record a trade on its page to start tracking it here.
          </p>
          <button
            type="button"
            onClick={openSearch}
            className="mt-5 inline-flex items-center rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
          >
            Search instruments
          </button>
        </section>
      ) : (
        <div className="flex flex-col lg:flex-row gap-4 lg:gap-6">
          <div className="flex flex-col gap-4 lg:flex-1 lg:min-w-0">
            <PortfolioMetrics
              accountingUnavailable={accountingUnavailable}
              portfolio={currentPortfolio}
              holdingsValue={summary?.holdingsValue}
              currentPnl={parseFloatFromString(summary?.current?.pnl || '0')}
              currentRoi={parseFloatFromString(summary?.current?.roi || '0')}
              valueChange24h={parseFloatFromString(summary?.valueChange24h || '0')}
              valueChangePercentage24h={parseFloatFromString(summary?.valueChangePercentage24h || '0')}
              positionsCount={openPositions.length}
              transactionsCount={transactionsCount}
              changeLabel="Day change"
            />
            {!accountingUnavailable && <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
              <PortfolioBreakdownBar
                investedAmount={summary?.current?.basisCost}
                holdingsValue={summary?.holdingsValue}
                realisedAmount={summary?.current?.proceeds}
                currency={currentPortfolio?.currency}
              />
            </div>}
          </div>
          <div className="flex flex-col gap-4 lg:flex-1 lg:min-w-0">
            <CategoryBreakdown
              title="Instrument types"
              categories={typeCategories}
              animationKey="stock-instrument-types"
              onSelect={(_, instrumentType) => navigate(STOCK_POSITIONS_ROUTE, { state: { instrumentType } })}
            />
            <CategoryBreakdown
              title="Sectors"
              categories={sectorCategories}
              animationKey="stock-sectors"
              onSelect={(_, sector) => navigate(STOCK_POSITIONS_ROUTE, { state: { sector } })}
            />
            <LocationBreakdown locationCategories={locationCategories} positionsPath={STOCK_POSITIONS_ROUTE} />
          </div>
        </div>
      )}
    </div>
  );
};

export default StocksDashboardPage;
